import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcon, AppIcons } from "./AppIcons";

// AppTheme - light, dark, or whatever the device says (design system, 2026-09-30). The RULES and the MARKUP, habitat-free
// (the Bridge suite reads them); the browser half that stores the choice and sets the attribute is `AppThemeRun`.
//
// ⭐ HOW IT WORKS: the tokens (`AppTokens.Css`, copied into app.css) are light on `:root`, dark for the system's dark mode
// unless `<html data-theme="light">`, and dark whenever `<html data-theme="dark">`. So "system" = no attribute at all;
// the owner's choice is kept in `localStorage` "wac.theme" and put on `<html>` - and on the contact frame's `<html>`
// (`Mark`), whose sheet (`AppTokens.FrameCss`) follows the same attribute.
//
// ⛔ NO INLINE STYLE, NO <style>: the choice is one attribute; the colours are the stylesheet's.

export class AppThemeRules {
    /** The key in `localStorage`. */
    public static readonly StoreKey: string = "wac.theme";
    public static readonly System: string = "system";
    public static readonly Light: string = "light";
    public static readonly Dark: string = "dark";
    /** The attribute on `<html>`; absent = follow the device. */
    public static readonly Attr: string = "data-theme";
    /** On each control button: which theme it picks. */
    public static readonly PickAttr: string = "data-wac-theme";
    public static readonly GroupId: string = "wac-theme";

    /** A stored value as one of the three - anything else (nothing stored, a typo, another app's value) is "system". */
    public static Parse = (Stored: string | null): string => {
        if (getCoreUtils().KErrorMode === true) { return AppThemeRules.System; }
        try {
            const Text: string = nz(Stored).trim();
            if (Text === AppThemeRules.Light || Text === AppThemeRules.Dark) { return Text; }
            return AppThemeRules.System;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRules.Parse");
            return AppThemeRules.System;
        }
    };

    /** The attribute's value for a choice - "" (no attribute) for "system". */
    public static AttrValue = (Choice: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parsed: string = AppThemeRules.Parse(Choice);
            if (Parsed === AppThemeRules.System) { return ""; }
            return Parsed;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRules.AttrValue");
            return "";
        }
    };

    /**
     * A page's markup with the choice on its `<html>` - for the contact frame, built before it loads. "system" = the
     * page unchanged. Only the FIRST `<html` tag is touched, and only when it has no theme of its own.
     */
    public static Mark = (Html: string, Choice: string): string => {
        if (getCoreUtils().KErrorMode === true) { return Html; }
        try {
            const Value: string = AppThemeRules.AttrValue(Choice);
            if (Value.length === 0) { return Html; }
            const Text: string = nz(Html);
            const At: number = Text.search(/<html\b/i);
            if (At < 0) { return Text; }
            const Close: number = Text.indexOf(">", At);
            if (Close < 0 || Text.substring(At, Close).indexOf(AppThemeRules.Attr + "=") >= 0) { return Text; }
            const Tag: number = At + "<html".length;
            return Text.substring(0, Tag) + " " + AppThemeRules.Attr + "=\"" + Value + "\"" + Text.substring(Tag);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRules.Mark");
            return Html;
        }
    };
}

/** The footer's theme control: three icon buttons in one group, the chosen one pressed. */
export class AppThemeView {
    /** The words each choice is known by - its tooltip and its accessible name. */
    public static Words = (Choice: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Choice === AppThemeRules.Light) { return "Light"; }
            if (Choice === AppThemeRules.Dark) { return "Dark"; }
            return "Match my device";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeView.Words");
            return "";
        }
    };

    public static IconOf = (Choice: string): AppIcon => {
        if (getCoreUtils().KErrorMode === true) { return AppIcons.Monitor; }
        try {
            if (Choice === AppThemeRules.Light) { return AppIcons.Sun; }
            if (Choice === AppThemeRules.Dark) { return AppIcons.Moon; }
            return AppIcons.Monitor;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeView.IconOf");
            return AppIcons.Monitor;
        }
    };

    /** One row per theme choice (System, Light, Dark), `Row` given the choice and "true"/"false" for the chosen one.
     *  Shared by the footer control and the top bar's settings menu. */
    public static Rows = (Chosen: string, Row: (One: string, On: string) => string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Now: string = AppThemeRules.Parse(Chosen);
            const Out: string[] = [];
            for (const One of [AppThemeRules.System, AppThemeRules.Light, AppThemeRules.Dark]) {
                Out.push(Row(One, One === Now ? "true" : "false"));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeView.Rows");
            return [];
        }
    };

    public static Control = (Chosen: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<span class=\"themepick\" role=\"group\" aria-label=\"Theme\" id=\"" + AppThemeRules.GroupId + "\">"];
            Out.push(...AppThemeView.Rows(Chosen, AppThemeView.ControlRow));
            Out.push("</span>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeView.Control");
            return "";
        }
    };

    private static ControlRow = (One: string, Pressed: string): string => {
        const Words: string = kHTMLCore.EscapeAttr(AppThemeView.Words(One));
        return "<button type=\"button\" class=\"btn ghost icon sm\" " + AppThemeRules.PickAttr + "=\"" + One
            + "\" aria-pressed=\"" + Pressed + "\" aria-label=\"" + Words + "\" title=\"" + Words + "\">"
            + AppIcons.Svg(AppThemeView.IconOf(One), 16) + "</button>";
    };
}
